/**
 * custom-pickup-ui.js
 * Unified pickup availability block for <ccc-pickup-availability>.
 *
 * Logic:
 *   In-stock variant               → ✅ ready line (pickupReadyLine below)
 *   Preorder (OOS + continue)      → ❌ Ready for pickup around [dispatch range]
 *   Sold out                       → ❌ Unavailable for pickup
 *
 * The in-stock ready line is clock-aware: "Usually ready in 2 hours" only
 * inside the weekday window, otherwise "Ready to collect today / tomorrow /
 * Monday from 9:30am". Liquid paints it first on the shop clock
 * (snippets/custom-pickup-ready-line.liquid); this re-runs the same rule on
 * the customer's clock, in Sydney time, so a CDN-cached page from 2:55pm
 * still rolls over at the cutoff.
 */

import { ThemeEvents } from '@theme/events';
import { getPickupMessage, getRestockEta, resolveDispatchLookupKey } from '@theme/custom-dispatch-manager';

/**
 * "9:30am" → 930 on an HHMM scale. Falls back to 9:30 if unparseable.
 * @param {string} text
 * @returns {number}
 */
function parseReadyFrom(text) {
  const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i.exec(text || '');
  if (!m) return 930;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = (m[3] || '').toLowerCase();
  if (ampm === 'pm' && h < 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  return h * 100 + min;
}

/**
 * Mirrors snippets/custom-pickup-ready-line.liquid — keep the two in step.
 *   Mon-Fri, ready time → cutoff   : openText
 *   Mon-Fri before the ready time  : Ready to collect today from …
 *   Mon-Thu after the cutoff       : Ready to collect tomorrow from …
 *   Fri after the cutoff, Sat, Sun : Ready to collect Monday from …
 * @param {string} openText
 * @param {string} readyFrom  e.g. "9:30am"
 * @param {number} cutoffHour 24h, e.g. 15
 * @returns {string}
 */
export function pickupReadyLine(openText, readyFrom, cutoffHour) {
  let dow = '';
  let hm = 0;
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    dow = get('weekday');
    hm = (parseInt(get('hour'), 10) % 24) * 100 + parseInt(get('minute'), 10);
  } catch {
    return openText;
  }

  const cutoff = (cutoffHour || 15) * 100;
  const readyHm = parseReadyFrom(readyFrom);
  let day = '';
  if (dow === 'Sat' || dow === 'Sun') {
    day = 'Monday';
  } else if (hm >= cutoff) {
    day = dow === 'Fri' ? 'Monday' : 'tomorrow';
  } else if (hm < readyHm) {
    day = 'today';
  }
  return day ? `Ready to collect ${day} from ${readyFrom}` : openText;
}

class CccPickupAvailability extends HTMLElement {
  connectedCallback() {
    this._productType = this.dataset.productType || '';
    this._vendor = this.dataset.vendor || '';
    this._printOnDemand = this.dataset.printOnDemand === 'true';
    this._variants = this._parseVariants();

    this._iconAvailable = this.querySelector('[data-ccpa-icon-available]');
    this._iconUnavailable = this.querySelector('[data-ccpa-icon-unavailable]');
    this._statusEl = this.querySelector('[data-ccpa-status]');
    this._titleEl = this.querySelector('[data-ccpa-title]');
    // The heading also holds the (i) toggle, so only this span is rewritten.
    this._titleTextEl = this.querySelector('[data-ccpa-title-text]') || this._titleEl;
    this._infoToggle = this.querySelector('[data-ccpa-info-toggle]');
    this._infoText = this.querySelector('[data-ccpa-info]');

    this._soldOutMsg = this.dataset.soldOutMsg || 'No stock currently available';
    this._pickupTime = this._readyLine();

    // Collection details disclosure beside the in-stock line.
    if (this._infoToggle && this._infoText) {
      this._boundInfoToggle = () => this._toggleInfo();
      this._infoToggle.addEventListener('click', this._boundInfoToggle);
    }

    const initialId = this._getInitialVariantId();
    if (initialId) this._update(initialId);

    this._boundSelectedHandler = (e) => {
      if (!this._isSameProductContext(e)) return;

      const id = e.detail?.resource?.variantId;
      if (!id) return;

      clearTimeout(this._debounce);
      this._update(id);
    };
    this._boundHandler = (e) => {
      const id = e.detail?.resource?.id;
      if (id) {
        clearTimeout(this._debounce);
        this._debounce = setTimeout(() => this._update(id), 40);
      }
    };
    document.addEventListener(ThemeEvents.variantSelected, this._boundSelectedHandler);
    document.addEventListener(ThemeEvents.variantUpdate, this._boundHandler);
  }

  disconnectedCallback() {
    if (this._boundSelectedHandler) {
      document.removeEventListener(ThemeEvents.variantSelected, this._boundSelectedHandler);
    }

    if (this._boundHandler) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._boundHandler);
    }

    if (this._infoToggle && this._boundInfoToggle) {
      this._infoToggle.removeEventListener('click', this._boundInfoToggle);
    }
  }

  _readyLine() {
    return pickupReadyLine(
      this.dataset.pickupTime || 'Usually ready in 2 hours',
      this.dataset.readyFrom || '9:30am',
      parseInt(this.dataset.cutoffHour, 10) || 15
    );
  }

  _parseVariants() {
    try {
      const el = this.querySelector('[data-ccpa-variants]');
      return el ? JSON.parse(el.textContent) : [];
    } catch {
      return [];
    }
  }

  _getInitialVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = parseInt(urlParams.get('variant'), 10);
    if (fromUrl) return fromUrl;

    const input = document.querySelector('input[name="id"]');
    if (input) return parseInt(input.value, 10);

    return this._variants.length ? this._variants[0].id : null;
  }

  async _update(variantId) {
    const variant = this._variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    this._pendingId = variantId;

    // Mirror snippets/custom-delayed-line-kind.liquid: print-on-demand only
    // delays pickup while Shopify inventory tracking is off for this variant.
    // Tracked stock on hand ships/collects today even on a Phaser3D product.
    const isTracked = Boolean(variant.inventory_management);
    const isContinuePreorder =
      isTracked && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue';
    const isPreorder =
      isContinuePreorder || (this._printOnDemand && !isTracked && variant.available);
    const isAvailable = variant.available && !isPreorder;
    const isSoldOut = !variant.available && !isPreorder;

    if (isPreorder || isSoldOut) {
      this._showUnavailable();
    } else {
      this._showAvailable();
    }

    if (isAvailable) {
      // Re-evaluated on every switch so a page left open across the cutoff
      // rolls over on the next interaction.
      this._pickupTime = this._readyLine();
      this._setStatus('available', this._pickupTime, { info: true });
    } else if (isPreorder) {
      const lookupKey = resolveDispatchLookupKey(this._productType, this._vendor);
      const restockOptions = { estimatedRestockDate: variant.estimated_restock_date || null };
      const msg = getPickupMessage(lookupKey, restockOptions);
      this._setStatus('unavailable', `${msg.charAt(0).toUpperCase()}${msg.slice(1)}.`);
    } else {
      // Sold out and not oversellable. Pickup stays unavailable either way —
      // the restock date only adds when it is worth checking back.
      const restockEta = isTracked ? getRestockEta(variant.estimated_restock_date) : null;
      this._setStatus(
        'unavailable',
        restockEta ? `${this._soldOutMsg} · ${restockEta.phrase}` : this._soldOutMsg
      );
    }

    if (this._titleEl) {
      this._titleTextEl.textContent = isAvailable ? 'Pickup available' : 'Pickup not available';
    }
  }

  _showAvailable() {
    if (this._iconAvailable) this._iconAvailable.hidden = false;
    if (this._iconUnavailable) this._iconUnavailable.hidden = true;
  }

  _showUnavailable() {
    if (this._iconAvailable) this._iconAvailable.hidden = true;
    if (this._iconUnavailable) this._iconUnavailable.hidden = false;
  }

  /**
   * Updates the status TEXT only. The info toggle and its text are siblings
   * of the status line inside the bar, so they survive; `info` says whether
   * they apply to this state (only the in-stock ready line).
   * @param {'available'|'unavailable'} type
   * @param {string} text
   * @param {{info?: boolean}} [opts]
   */
  _setStatus(type, text, opts = {}) {
    if (!this._statusEl) return;
    this._statusEl.classList.toggle('ccpa-status--available', type === 'available');
    this._statusEl.classList.toggle('ccpa-status--unavailable', type !== 'available');
    const textEl = this._statusEl.querySelector('[data-ccpa-status-text]');
    if (textEl) {
      textEl.textContent = text;
    } else {
      this._statusEl.textContent = text;
    }

    if (this._infoToggle && this._infoText) {
      const show = Boolean(opts.info);
      this._infoToggle.hidden = !show;
      if (!show) this._toggleInfo(false);
    }
  }

  /**
   * @param {boolean} [force] open (true) / close (false); omit to toggle
   */
  _toggleInfo(force) {
    if (!this._infoToggle || !this._infoText) return;
    const open = typeof force === 'boolean' ? force : this._infoText.hidden;
    this._infoText.hidden = !open;
    this._infoToggle.setAttribute('aria-expanded', String(open));
  }

  _isSameProductContext(event) {
    if (!(event.target instanceof HTMLElement)) return false;

    const sourceSection = event.target.closest('.shopify-section, dialog');
    const hostSection = this.closest('.shopify-section, dialog');

    return Boolean(sourceSection && hostSection && sourceSection === hostSection);
  }
}

if (!customElements.get('ccc-pickup-availability')) {
  customElements.define('ccc-pickup-availability', CccPickupAvailability);
}
